import React from 'react';
import {useQuery} from '@tanstack/react-query';
import {Loader2, Search} from 'lucide-react';
import clsx from 'clsx';
import AdminService from '@/modules/admin/services/adminService';
import type {AdminLoincCode, AdminLoincCodesResponse} from '@/shared/types/api';
import {looksLikeLoincCode} from '@mld/shared/loinc/codeShape';

const PAGE_SIZE = 50;

/** A name shorter than this matches most of a 112k-row reference, and the
 *  full-canon query keeps its usage counts (measured 400-900ms). Below the
 *  threshold we stay on the small in-use slice, as this screen always did.
 *  A code is exempt: it is exact, and short ones like `1-1` are real. */
const MIN_NAME_SEARCH = 3;

/** Total places a code reaches a patient. Sums both result tables — most codes
 *  arrive via OHD, so counting only `results` would show 0 for rows plainly in
 *  use and make the whole screen look wrong. */
const usageCount = (row: AdminLoincCode): number => row.resultCount + row.ohdResultCount;

/**
 * LOINC's four lifecycle statuses do not mean the same thing, and the panel
 * used to badge every non-ACTIVE one in red. TRIAL means "new, may still
 * change" — the opposite of DEPRECATED/DISCOURAGED — and there are 5,436 of
 * them, so a single red treatment cried wolf on the whole trial set. ACTIVE
 * gets no badge: it is the unremarkable case.
 */
const STATUS_BADGE: Record<string, {className: string; title: string}> = {
    TRIAL: {
        className: 'bg-amber-100 text-amber-800',
        title: 'Provisional — newly published by LOINC and may still change or be withdrawn. Usable; prefer an ACTIVE code where one exists.',
    },
    DISCOURAGED: {
        className: 'bg-red-100 text-red-700',
        title: 'LOINC discourages this code — do not use it for new data.',
    },
    DEPRECATED: {
        className: 'bg-red-100 text-red-700',
        title: 'Withdrawn by LOINC — do not use it.',
    },
};

/** An unrecognised status is reported, not judged: LOINC could add one, and
 *  guessing "dangerous" would repeat the mistake this map exists to fix. */
const UNKNOWN_STATUS_BADGE = {
    className: 'bg-gray-100 text-gray-700',
    title: 'Status reported by LOINC.',
};

const statusBadge = (status: string) => STATUS_BADGE[status.trim().toUpperCase()] ?? UNKNOWN_STATUS_BADGE;

/**
 * Read-only LOINC reference.
 *
 * This page used to EDIT `loinc_codes.display_label` and `.category`, and its
 * header claimed those drove what patients see. They did not — the patient
 * card always read `biomarker_display_rules`, so an admin could save a label
 * here, watch it stick, and change nothing a patient sees. Since the name-canon
 * consolidation (docs/superpowers/plans/2026-08-25-biomarker-name-canon.md)
 * BioMatch, synthetic results and the warehouse read the display rules too,
 * so loinc_codes carries no product data and this page is exactly what it
 * looks like: the reference list of codes this deployment uses, with usage
 * counts. Names and categories are edited in ONE place — the Display rules
 * tab — which is where the per-row "Curate" link goes (`onCurate`, carrying
 * the row's code so the rules tab can jump to it or pre-fill a new rule).
 */
const AdminLoincCodesPanel: React.FC<{onCurate?: (loinc: string) => void}> = ({onCurate}) => {
    const [search, setSearch] = React.useState('');
    const [debouncedSearch, setDebouncedSearch] = React.useState('');
    const [page, setPage] = React.useState(0);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(0);
        }, 250);
        return () => clearTimeout(timer);
    }, [search]);

    const term = debouncedSearch.trim();
    const searchesWholeReference = looksLikeLoincCode(term) || term.length >= MIN_NAME_SEARCH;

    const queryKey = ['admin-loinc-codes', debouncedSearch, page, searchesWholeReference];
    const listQuery = useQuery<AdminLoincCodesResponse>({
        queryKey,
        queryFn: () =>
            AdminService.listLoincCodes({
                search: debouncedSearch || undefined,
                needsReview: false,
                limit: PAGE_SIZE,
                offset: page * PAGE_SIZE,
                // SEARCHING spans the whole published reference: answering
                // "is 5950-0 real?" is impossible from the in-use slice, which
                // returns silence either way. BROWSING (no term) stays on the
                // codes we actually hold data for — an unfiltered list of
                // 112,405 codes alphabetically is not a useful landing page,
                // and it is the in-use set that carries the usage counts worth
                // reading.
                scope: searchesWholeReference ? 'all' : 'in-use',
            }),
    });

    const data = listQuery.data;
    const rows = data?.rows ?? [];
    const total = data?.total ?? 0;
    const lastPage = Math.max(Math.ceil(total / PAGE_SIZE) - 1, 0);

    return (
        // Card + section-header shape copied from StorefrontProductBuilder so
        // this page sits in the console the same way its siblings do.
        <div className="space-y-6">
            <div className="rounded-lg border border-mld-panel-border bg-mld-menu-background p-6 shadow-sm">
                <div className="mb-6 flex flex-wrap items-start justify-between gap-4 border-b border-mld-panel-border pb-4">
                    <div>
                        <h2 className="text-xl font-semibold text-mld-text-primary">LOINC reference</h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Read-only. Lists the codes this deployment uses, with how often each reaches a patient
                            &mdash; but{' '}
                            <span className="font-medium">searching spans the whole LOINC reference imported here</span>
                            , so you can check a code before assigning it. A code showing{' '}
                            <span className="font-medium">0 results</span> is one we simply hold no data for, not a
                            wrong code. Names and health categories are edited in the Display rules tab &mdash; one
                            editor, one source of truth.
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <label className="relative block w-72">
                        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                        <input
                            aria-label="Search LOINC codes"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Search code, label or official name"
                            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-mld-primary"
                        />
                    </label>
                </div>

                <div className="mt-4 overflow-hidden rounded-lg border border-mld-panel-border">
                    <div className="max-h-[60vh] overflow-auto">
                        <table className="min-w-full divide-y divide-mld-panel-border text-sm">
                            <thead className="sticky top-0 bg-mld-menu-background text-left text-[11px] uppercase tracking-[0.12em] text-gray-500">
                                <tr>
                                    <th className="px-4 py-2">Code</th>
                                    <th className="px-4 py-2">Official name</th>
                                    <th className="w-32 px-4 py-2">Used by</th>
                                    <th className="w-56 px-4 py-2 text-right">Name &amp; category</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-mld-panel-border bg-white">
                                {listQuery.isLoading ? (
                                    <tr>
                                        <td colSpan={4} className="px-4 py-10 text-center text-gray-500">
                                            <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                                        </td>
                                    </tr>
                                ) : listQuery.isError ? (
                                    <tr>
                                        <td colSpan={4} className="px-4 py-10 text-center text-red-600">
                                            Unable to load LOINC codes.
                                        </td>
                                    </tr>
                                ) : rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-4 py-10 text-center text-gray-500">
                                            {looksLikeLoincCode(debouncedSearch) ? (
                                                <>
                                                    <span className="font-mono">{debouncedSearch.trim()}</span> is not
                                                    in this environment&rsquo;s LOINC reference.
                                                    <div className="mt-1 text-xs">
                                                        Either the code is wrong, or the reference has not been imported
                                                        here &mdash; run <code>loinc:import</code> to tell the two
                                                        apart.
                                                    </div>
                                                </>
                                            ) : (
                                                'No codes match.'
                                            )}
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((row) => (
                                        <tr key={row.loinc} className="align-top">
                                            <td className="px-4 py-3">
                                                <div className="font-mono text-xs text-mld-text-primary">
                                                    {row.loinc}
                                                </div>
                                                {row.status && row.status.trim().toUpperCase() !== 'ACTIVE' ? (
                                                    <span
                                                        title={statusBadge(row.status).title}
                                                        className={clsx(
                                                            'mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium',
                                                            statusBadge(row.status).className
                                                        )}>
                                                        {row.status}
                                                    </span>
                                                ) : null}
                                            </td>

                                            <td className="px-4 py-3 text-mld-text-primary">
                                                {row.longName ?? row.displayLabel}
                                            </td>

                                            <td className="px-4 py-3 text-gray-500">
                                                {usageCount(row).toLocaleString()} result
                                                {usageCount(row) === 1 ? '' : 's'}
                                                {row.assayCount > 0 ? (
                                                    <div className="text-xs">
                                                        {row.assayCount} assay{row.assayCount === 1 ? '' : 's'}
                                                    </div>
                                                ) : null}
                                            </td>

                                            <td className="px-4 py-3 text-right">
                                                {/* Always visible — the old pencil was opacity-0 until
                                                    hover, which made rows look read-only on desktop and
                                                    left them genuinely uneditable on touch devices. */}
                                                <button
                                                    type="button"
                                                    onClick={() => onCurate?.(row.loinc)}
                                                    className="text-sm font-medium text-mld-primary hover:underline">
                                                    Curate in Display rules &rarr;
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
                    <span>
                        {total.toLocaleString()} code{total === 1 ? '' : 's'} in use
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            disabled={page === 0}
                            onClick={() => setPage((p) => Math.max(p - 1, 0))}
                            className="rounded-md border border-mld-panel-border px-3 py-1 disabled:opacity-40">
                            Previous
                        </button>
                        <span>
                            Page {page + 1} of {lastPage + 1}
                        </span>
                        <button
                            type="button"
                            disabled={page >= lastPage}
                            onClick={() => setPage((p) => Math.min(p + 1, lastPage))}
                            className="rounded-md border border-mld-panel-border px-3 py-1 disabled:opacity-40">
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminLoincCodesPanel;
